// The audit filters as one plain object, its API parameters (phase 4
// ParseValues names plus Task 1's action and actorGroup) and its URL form,
// so a link reproduces the view (spec §5, §7 "filters to query string").
//
// The URL is input from anywhere (a pasted link, an old bookmark, a typo),
// so filtersFromSearch keeps only values of the shape the server accepts and
// drops the rest quietly; it never throws.
import { dateTimeText, dayOf as zoneDayOf, dayWindow } from '../date/local-time'

export const RANGES = [
  { value: '24h', label: 'Last 24 hours', ms: 864e5 },
  { value: '7d', label: 'Last 7 days', ms: 7 * 864e5 },
  { value: '30d', label: 'Last 30 days', ms: 30 * 864e5 },
  { value: '90d', label: 'Last 90 days', ms: 90 * 864e5 },
  { value: '365d', label: 'Last 12 months', ms: 365 * 864e5 },
  { value: 'custom', label: 'Custom range…' }
]

export const EMPTY_FILTERS = {
  q: '',
  actors: [],
  groups: [],
  recordTypes: [],
  categories: [],
  topics: [],
  actions: [],
  apps: [],
  countries: [],
  ip: '',
  range: '30d',
  from: '',
  to: '',
  includeViews: false,
  includeAutomatic: false,
  request: '',
  session: '',
  // Insights clicks (content's Task 10 follow-up filters): the records an
  // entry is about, and the entry's verb and outcome as the panels show them
  recordFilters: [],
  verbs: [],
  outcomes: [],
  // "<name> and everything in it" (owner 2026-10-09): a course or folder
  // and everything under it, nodes:<id> (content's within filter)
  within: ''
}

const NO_DEFAULTS = {}

// The server's limits (content audit-entries params.go).
const MAX_TEXT = 200
const MAX_LIST = 50

const CATEGORIES = ['changes', 'publishing', 'people_access', 'security', 'sign_ins', 'views', 'email']
// content params.go: the outcomes, and the plain verbs (a verb may also be
// a category's label or key, as the changed panel shows it)
export const OUTCOMES = ['Changed', 'Viewed or opened', 'Signed in', 'Failed sign-in']
const text = v => v.length > 0 && v.length <= MAX_TEXT

// filter key: [URL key, API key, value check, at most, API separator]
const LIST = {
  actors: ['who', 'actor', v => /^(user|login):[1-9][0-9]{0,9}$/.test(v) || /^auto:.{1,200}$/.test(v), 100],
  groups: ['group', 'actorGroup', v => /^[1-9][0-9]{0,9}$/.test(v), 10],
  recordTypes: ['type', 'recordType', v => /^[a-z_]{1,64}$/.test(v), MAX_LIST],
  categories: ['cat', 'category', v => CATEGORIES.includes(v), MAX_LIST],
  topics: ['topic', 'topic', text, MAX_LIST],
  // an exact action (invite.created) or a family (invite.*): exact match on
  // the action an entry is shown as (Task 1's ruling)
  actions: ['action', 'action', v => /^[a-z_]+(\.[a-z_]+)*(\.\*)?$/.test(v) && v.length <= 100, MAX_LIST],
  apps: ['app', 'app', text, MAX_LIST],
  countries: ['geo', 'country', v => /^[A-Z]{2}$/.test(v), MAX_LIST],
  // <type>:<id>; an id may hold "," and "=", so the server reads "|"
  recordFilters: ['rec', 'recordFilter', v => /^[a-z_]{1,64}:[A-Za-z0-9_=,.:-]{1,128}$/.test(v), MAX_LIST, '|'],
  verbs: ['verb', 'verb', v => v.length <= 100 && /^[A-Za-z][A-Za-z &_-]*$/.test(v), MAX_LIST],
  outcomes: ['outcome', 'outcome', v => OUTCOMES.includes(v), OUTCOMES.length]
}
const TEXT = {
  q: ['q', 'q', () => true],
  ip: ['ip', 'ip', v => /^[0-9A-Fa-f:.]{2,45}(\/[0-9]{1,3})?$/.test(v)],
  request: ['request', 'requestID', v => /^[0-9A-Za-z-]{8,64}$/.test(v)],
  session: ['session', 'sessionID', v => /^[0-9A-Za-z-]{1,64}$/.test(v)],
  within: ['within', 'within', v => /^nodes:[1-9][0-9]{0,18}$/.test(v)]
}
const FLAG = { includeViews: ['views', 'includeViews'], includeAutomatic: ['auto', 'includeAutomatic'] }

// Every URL key the filters own; anything else in the query string (the
// page's ?view=table) belongs to the page.
export const FILTER_URL_KEYS = [...[LIST, TEXT, FLAG].flatMap(m => Object.values(m).map(([url]) => url)), 'range', 'from', 'to']

const isDay = v => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || '')) return false
  const d = new Date(`${v}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v
}

// A custom range's end is either a day in the reader's time zone
// (YYYY-MM-DD: the date pickers) or an exact instant (an Insights pivot:
// the panel's own window, or one day of the calendar, its local midnights),
// which is sent unchanged so the list is exactly what the panel counted.
// The server's instants (Go RFC 3339) may carry up to nanoseconds.
const INSTANT = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?Z$/
export const isInstant = v => {
  const m = INSTANT.exec(v || '')
  return Boolean(m) && isDay(m[1]) && !Number.isNaN(new Date(v).getTime())
}
// An instant as sent: kept exactly when it is already a full UTC instant
// with seconds (a server window may end inside a millisecond, and rounding
// it down would drop an entry there); otherwise normalised.
const instant = v => (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3,9}Z$/.test(v) ? v : new Date(v).toISOString())

// One day of a calendar counted in zone tz (the response's timeZone: the
// server buckets days in the reader's zone) as a custom range: the day's
// local midnights as exact instants, [00:00, next 00:00), 23 or 25 hours
// across a daylight-saving change (shared/date dayWindow). Never a UTC day
// shifted: that would list another day's entries than the cell counted.
export const zoneDay = (day, tz) => ({ range: 'custom', ...dayWindow(day, tz) })
// One such day within a window (a panel's from/to): a calendar cell counts
// only its part of the day when the window's edges aren't its midnights
// ("now" on the last day, or a window the cell's zone doesn't share).
export const zoneDayWithin = (day, from, to, tz) => {
  const d = zoneDay(day, tz)
  const start = from && Date.parse(from) > Date.parse(d.from) ? instant(from) : d.from
  const end = to && Date.parse(to) < Date.parse(d.to) ? instant(to) : d.to
  return { range: 'custom', from: start, to: end }
}
// An exact window (a panel's from/to) as a custom range.
export const utcWindow = (from, to) => ({ range: 'custom', from: instant(from), to: instant(to) })

// The server refuses a window over 366 days (content maxWindow).
export const MAX_RANGE_DAYS = 366
const MAX_RANGE_MS = MAX_RANGE_DAYS * 864e5

const customEnds = (f, now) => ({
  from: !f.from ? null : isInstant(f.from) ? new Date(f.from) : new Date(`${f.from}T00:00:00`),
  to: !f.to ? now : isInstant(f.to) ? new Date(f.to) : new Date(`${f.to}T23:59:59.999`)
})

// A custom range's window as instants: from (or null) and to (now when open).
export const customWindow = (f, now = new Date()) => customEnds(f, now)

// "Exactly Sep 29, 2026 at 12:00 AM MDT to Sep 30, 2026 at 12:00 AM MDT."
// (the reader's own times) for a range with an exact end, else null.
export const exactRangeText = f => {
  if (!f || f.range !== 'custom' || !(isInstant(f.from) || isInstant(f.to))) return null
  const from = isInstant(f.from) ? dateTimeText(f.from) : f.from || 'the start'
  const to = isInstant(f.to) ? dateTimeText(f.to) : f.to || 'now'
  return `Exactly ${from} to ${to}.`
}
// The day a picker shows for an end: an instant shows the reader's day
// holding it (end: an exclusive end, so a midnight shows the day before).
export const pickerDay = (v, end = false) => (isInstant(v) ? zoneDayOf(Date.parse(v) - (end ? 1 : 0)) : v || '')

// A custom range longer than the server allows (an open end is now).
export const rangeTooLong = (f, now = new Date()) => {
  if (f.range !== 'custom' || !f.from) return false
  const { from, to } = customEnds(f, now)
  return to.getTime() - from.getTime() > MAX_RANGE_MS
}

// A custom range's window as the API reads it (exact instants unchanged).
// clamp: move a too-long range's start in (the timeline); the export
// dialog refuses one instead.
export const customParams = (f, now = new Date(), { clamp = true } = {}) => {
  const out = {}
  const ends = customEnds(f, now)
  out.to = f.to && isInstant(f.to) ? instant(f.to) : ends.to.toISOString()
  // never send more than the server allows: keep the end, move the start
  if (ends.from) {
    out.from = clamp && rangeTooLong(f, now)
      ? new Date(ends.to.getTime() - MAX_RANGE_MS).toISOString()
      : isInstant(f.from) ? instant(f.from) : ends.from.toISOString()
  }
  return out
}

const windowOf = (f, now) => {
  if (f.range === 'all') return {}
  if (f.range === 'custom') {
    const out = customParams(f, now)
    if (!f.to) delete out.to
    return out
  }
  const r = RANGES.find(x => x.value === f.range) || RANGES[2]
  return { from: new Date(now.getTime() - r.ms).toISOString(), to: now.toISOString() }
}

export const filtersToParams = (f, now = new Date()) => {
  const p = {}
  Object.entries(LIST).forEach(([k, [, api, , , sep]]) => {
    if (f[k] && f[k].length) p[api] = f[k].join(sep || ',')
  })
  Object.entries(TEXT).forEach(([k, [, api]]) => {
    if (f[k] && f[k].trim()) p[api] = f[k].trim()
  })
  Object.entries(FLAG).forEach(([k, [, api]]) => {
    if (f[k]) p[api] = '1'
  })
  return { ...p, ...windowOf(f, now) }
}

// The URL's list separator is `|`, because app names may contain commas.
// defaults: a page's own default for a toggle ({ includeViews: true } on the
// Activity tab); the URL then holds only a departure from it (views=0).
export const filtersToSearch = (f, defaults = NO_DEFAULTS) => {
  const s = new URLSearchParams()
  Object.entries(LIST).forEach(([k, [url]]) => {
    if (f[k] && f[k].length) s.set(url, f[k].join('|'))
  })
  Object.entries(TEXT).forEach(([k, [url]]) => {
    if (f[k]) s.set(url, f[k])
  })
  Object.entries(FLAG).forEach(([k, [url]]) => {
    if (Boolean(f[k]) !== Boolean(defaults[k])) s.set(url, f[k] ? '1' : '0')
  })
  if (f.range && f.range !== EMPTY_FILTERS.range) s.set('range', f.range)
  if (f.range === 'custom') {
    if (f.from) s.set('from', f.from)
    if (f.to) s.set('to', f.to)
  }
  const t = s.toString()
  return t ? `?${t}` : ''
}

const readSearch = (search, defaults) => {
  const s = new URLSearchParams(typeof search === 'string' ? search : '')
  const f = { ...EMPTY_FILTERS }
  Object.entries(LIST).forEach(([k, [url, , ok, max]]) => {
    const raw = s.get(url)
    if (!raw) return
    f[k] = [...new Set(raw.split('|').map(v => v.trim()).filter(ok))].slice(0, max)
  })
  Object.entries(TEXT).forEach(([k, [url, , ok]]) => {
    const v = (s.get(url) || '').slice(0, MAX_TEXT)
    if (v.trim() && ok(v.trim())) f[k] = v
  })
  Object.entries(FLAG).forEach(([k, [url]]) => {
    f[k] = s.has(url) ? s.get(url) === '1' : Boolean(defaults[k])
  })
  const range = s.get('range')
  if (RANGES.some(r => r.value === range) || range === 'all') f.range = range
  if (f.range === 'custom') {
    const end = v => (isDay(v) ? v : isInstant(v) ? instant(v) : '')
    f.from = end(s.get('from'))
    f.to = end(s.get('to'))
    if (rangeTooLong(f)) f.from = ''
  }
  return f
}

export const filtersFromSearch = (search, defaults = NO_DEFAULTS) => {
  try {
    return readSearch(search, defaults || NO_DEFAULTS)
  } catch (e) {
    return { ...EMPTY_FILTERS }
  }
}

export const applyPivot = (f, patch) => ({ ...f, ...patch })

// Whether a pivot patch survives exactly: every value read back from the
// URL as given (the URL checks drop what they can't read, which would
// widen the list), and no value of a comma-joined API list holding a comma
// (the server splits on it). An Insights element whose patch doesn't is
// offered no click.
export const pivotable = patch => {
  if (!patch || typeof patch !== 'object') return false
  const next = { ...EMPTY_FILTERS, ...patch }
  const back = filtersFromSearch(filtersToSearch(next))
  return Object.keys(patch).every(k => {
    if (!(k in EMPTY_FILTERS)) return false
    if (LIST[k] && !LIST[k][4] && next[k].some(v => String(v).includes(','))) return false
    if (k === 'from' || k === 'to') return !next[k] || back[k] === (isInstant(next[k]) ? instant(next[k]) : next[k])
    return JSON.stringify(back[k]) === JSON.stringify(next[k])
  })
}

// The narrowings an Insights click adds that have no control in the
// filter column (verb, outcome, records), in words, for a page's
// "Showing only ..." line.
// (This module imports only shared/date/local-time, which imports nothing
// -- filters-model.zone.test.js loads both in a child process -- so the
// count is written as sso-workspace/format's formatCount does.)
export const insightParts = f => {
  const parts = []
  if (f.verbs && f.verbs.length) parts.push({ key: 'verb', text: f.verbs.join(' or ') })
  if (f.outcomes && f.outcomes.length) parts.push({ key: 'outcome', text: `outcome ${f.outcomes.join(' or ')}` })
  if (f.recordFilters && f.recordFilters.length) {
    parts.push(f.recordFilters.length === 1
      ? { key: 'records', text: 'one record', code: f.recordFilters[0] }
      : { key: 'records', text: `${f.recordFilters.length.toLocaleString('en-US')} records` })
  }
  return parts
}
// ...and what "Show everything" clears of them.
export const NO_INSIGHT_NARROWING = { verbs: [], outcomes: [], recordFilters: [] }

// "All content" (a section's ⋮ Audit log): every record type of the
// content tree -- nodes and what hangs off them. The page says so in words
// (contentPart), since a list of record types reads as nothing in
// particular.
export const CONTENT_RECORD_TYPES = [
  'nodes', 'assets', 'asset_versions', 'node_course_versions', 'node_users', 'node_groups',
  'chatbot_configs', 'chatbot_documents', 'analytic_topics', 'analytics'
]
export const isAllContent = f =>
  Boolean(f.recordTypes) && f.recordTypes.length === CONTENT_RECORD_TYPES.length && CONTENT_RECORD_TYPES.every(t => f.recordTypes.includes(t))
export const contentPart = f => (isAllContent(f) ? [{ key: 'content', text: 'all content' }] : [])
export const contentAuditSearch = () => filtersToSearch({ ...EMPTY_FILTERS, recordTypes: CONTENT_RECORD_TYPES })

// How many filters are set, other than the time range (the phone's
// "Filters (n)" button).
export const activeCount = f =>
  ['actors', 'groups', 'recordTypes', 'categories', 'apps', 'countries'].reduce((n, k) => n + (f[k] && f[k].length ? 1 : 0), 0) +
  (f.ip ? 1 : 0) +
  (f.includeViews ? 1 : 0) +
  (f.includeAutomatic ? 1 : 0)

// A learning-activity reader (caps.activityLog) never filters by IP address
// or location: the API answers 403 audit.forbidden_filter, so a link carrying
// ip= or geo= drops them instead.
export const withoutNetwork = (f, caps) => (caps && caps.activityLog ? { ...f, ip: '', countries: [] } : f)

// The page's time range in words, for every Insights panel's pill and
// sentences: a preset by its name ("Last 7 days", "in the last 7 days"),
// a custom range by its days ("Oct 6, 2026"; "Sep 1 – Sep 30"; "from Sep 1
// to Sep 30"). A day-picker end is the reader's own day, and so is an
// exact instant's (an exclusive midnight end is the day before). An open
// start is the server's default window (30 days to the end), as the
// timeline reads it. day: the one day the range covers, else null.
const DEFAULT_WINDOW_MS = 30 * 864e5
const localDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const dayOf = (v, end) => {
  if (isDay(v)) return v
  if (!isInstant(v)) return ''
  return zoneDayOf(Date.parse(v) - (end ? 1 : 0))
}
const dayWords = (day, year) => new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: year ? 'numeric' : undefined, timeZone: 'UTC' })
export const rangeView = (f, now = new Date()) => {
  const preset = RANGES.find(r => r.ms && r.value === (f && f.range))
  if (!f || f.range !== 'custom') {
    const r = preset || RANGES[2]
    return { label: r.label, span: `in the ${r.label.toLowerCase()}`, day: null }
  }
  const last = dayOf(f.to, true) || localDay(now)
  const end = f.to ? customEnds(f, now).to : now
  // an open start: 30 days before the end, as the reader's day
  const start = new Date(end.getTime() - DEFAULT_WINDOW_MS)
  const first = dayOf(f.from, false) || localDay(start)
  if (first >= last) {
    const text = dayWords(last, true)
    return { label: text, span: `on ${text}`, day: last }
  }
  const sameYear = first.slice(0, 4) === last.slice(0, 4)
  const thisYear = sameYear && Number(last.slice(0, 4)) === now.getFullYear()
  const a = dayWords(first, !sameYear)
  const b = dayWords(last, !thisYear)
  return { label: `${a} – ${b}`, span: `from ${a} to ${b}`, day: null }
}

// The params a panel reads for the page's range: the timeline's own, with
// an open start filled in as the server fills the timeline's (30 days to
// the end), so every panel counts exactly the timeline's window.
export const withWindow = (params, now = new Date()) => {
  if (!params || params.from) return params
  const to = params.to ? new Date(params.to) : now
  return { ...params, from: new Date(to.getTime() - DEFAULT_WINDOW_MS).toISOString(), ...(params.to ? {} : { to: now.toISOString() }) }
}

// A range's own URL keys (range, from, to) as one value, so a page can
// remember the range a calendar day replaced (?was=) and go back to it.
export const rangeKey = f => {
  const s = new URLSearchParams()
  s.set('range', f.range || EMPTY_FILTERS.range)
  if (f.range === 'custom') {
    if (f.from) s.set('from', f.from)
    if (f.to) s.set('to', f.to)
  }
  return s.toString()
}
export const rangeFromKey = key => {
  const f = filtersFromSearch(`?${key || ''}`)
  return { range: f.range, from: f.from, to: f.to }
}
export const sameRange = (a, b) => rangeKey(a) === rangeKey(b)
